import { Mapper } from '@/mapper';
import { Option } from '@/models/option';
import { PackagePathSegment } from '@/models/package-path-segment';
import { PathSegment } from '@/models/path-segment';
import { SimplePathSegment } from '@/models/simple-path-segment';
import { createEmptyStatefulRequest } from '@/models/stateful-request';
import { StoreState } from '@/store/index';
import { PathUtil } from '@/util/path-util';

const fcbId = +window.funeralConfigurator.fcbId;

export default {
   setChosenSegment(
      state: StoreState,
      segment: PathSegment | null) {
      if (segment instanceof PackagePathSegment) {
         segment.options.forEach(option => option.isPreSelected = true);
      }
      state.currentPathSegment = segment;
   },
   setSelectedSegment(
      state: StoreState,
      segment: PackagePathSegment | SimplePathSegment | null) {
      state.currentPathSegment
         ? state.currentPathSegment?.children.forEach(segment => segment.chosen = false)
         : state.pathSegments.forEach(segment => segment.chosen = false);

      if (!segment) {
         return;
      }
      segment.chosen = true;
   },
   addSegmentToLog(
      state: StoreState,
      segment: PathSegment) {
      // if the new segment is already in the log, the user has gone back
      if (state.segmentLog.includes(segment)) {
         return;
      }

      // check if the user is on the same path or has gone back and
      // chosen another path
      if (PathUtil.isPathIntact(state.segmentLog, segment)) {
         state.segmentLog.push(segment);
         return;
      }

      // clear the log until the path is valid again
      PathUtil.truncateLogUntilValid(state.segmentLog, segment);

      // reset additional option selection if the user has gone back
      // more than the last element in the tree
      if (segment.children.length > 1) {
         state.options.forEach((option: Option) => {
            option.isSelected = false;
         });
      }

      state.segmentLog.push(segment);
   },
   enablePathSummary(
      state: StoreState,
      enable: boolean): void {
      state.summaryAvailable = enable;
   },
   setPathSummaryActive(
      state: StoreState,
      enable: boolean): void {
      state.summaryActive = enable;
   },
   setOptionSelected(
      state: StoreState,
      payload: {selected: boolean; optionId: number}): void {
      const optionIdx = state.options.findIndex(option => option.id === payload.optionId);
      if (optionIdx === -1) {
         console.error('You clicked on a non existent option');
         return;
      }
      const newOption = {...state.options[optionIdx]};
      newOption.isSelected = payload.selected;
      state.options[optionIdx] = newOption;
   },
   setPrice(
      state: StoreState,
      payload: number): void {
      state.calculatedPrice = payload;
   },
   setExternalPricePart(state: StoreState, payload: number): void
   {
       state.calculatedExternalPricePart = payload;
   },
   callbackRequestReset(state: StoreState): void {
      state.callbackRequest = createEmptyStatefulRequest();
   },
   callbackRequestStarted(state: StoreState): void {
      // make sure the callbackRequest state is a new, empty one.
      const callbackRequest = createEmptyStatefulRequest();
      callbackRequest.isInProgress = true;

      state.callbackRequest = callbackRequest;
   },
   callbackRequestFailed(
      state: StoreState,
      payload: {errorMessage: string}): void {
      state.callbackRequest = {
         ...state.callbackRequest,
         isInProgress: false,
         hasFailed: true,
         errorMessage: payload.errorMessage
      };
   },
   callbackRequestSucceeded(state: StoreState): void {
      state.callbackRequest = {
         ...state.callbackRequest,
         isInProgress: false,
         hasSucceeded: true
      };
   },
   quoteRequestReset(state: StoreState): void {
      state.quoteRequest = createEmptyStatefulRequest();
   },
   quoteRequestStarted(state: StoreState): void {
      // make sure the callbackRequest state is a new, empty one.
      const quoteRequest = createEmptyStatefulRequest();
      quoteRequest.isInProgress = true;

      state.quoteRequest = quoteRequest;
   },
   quoteRequestFailed(
      state: StoreState,
      payload: {errorMessage: string}): void {
      state.quoteRequest = {
         ...state.quoteRequest,
         isInProgress: false,
         hasFailed: true,
         errorMessage: payload.errorMessage
      };
   },
   quoteRequestSucceeded(state: StoreState): void {
      state.quoteRequest = {
         ...state.quoteRequest,
         isInProgress: false,
         hasSucceeded: true
      };
   },
   flash(state: StoreState): void {
      state.flashActive = true;
      setTimeout(() => {
         state.flashActive = false;
      }, 1000);
   },
   reset(state: StoreState): void {
      const mapper = new Mapper(state.journeys, state.options, state.optionGroups);
      state.pathSegments = mapper.buildPathSegmentTree(fcbId);
      state.segmentLog = [];
      state.currentPathSegment = null;
      state.options.forEach((option: Option) => {
         option.isSelected = false;
      });
   }
};

export enum Mutations {
   AddSegmentToLog = 'addSegmentToLog',
   SetPrice = 'setPrice',
   CallbackRequestFailed = 'callbackRequestFailed',
   CallbackRequestReset = 'callbackRequestReset',
   CallbackRequestStarted = 'callbackRequestStarted',
   CallbackRequestSucceeded = 'callbackRequestSucceeded',
   EnablePathSummary = 'enablePathSummary',
   Flash = 'flash',
   Reset = 'reset',
   SetOptionSelected = 'setOptionSelected',
   SetSelectedSegment = 'setSelectedSegment',
   SetChosenSegment = 'setChosenSegment',
   SetPathSummaryActive = 'setPathSummaryActive',
   SetExternalPricePart = 'setExternalPricePart',
   QuoteRequestFailed = 'quoteRequestFailed',
   QuoteRequestReset = 'quoteRequestReset',
   QuoteRequestStarted = 'quoteRequestStarted',
   QuoteRequestSucceeded = 'quoteRequestSucceeded',
}
